import React from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../App'
import './Layout.css'

const nav = [
  { to: '/', label: 'Dashboard', end: true, icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="5" height="5" rx="1.2"/><rect x="9" y="2" width="5" height="5" rx="1.2"/>
      <rect x="2" y="9" width="5" height="5" rx="1.2"/><rect x="9" y="9" width="5" height="5" rx="1.2"/>
    </svg>
  )},
  { to: '/clients', label: 'Clients', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="5.5" r="2.5"/><path d="M2.5 13c0-2.5 2.5-4.5 5.5-4.5s5.5 2 5.5 4.5"/>
    </svg>
  )},
]

const projectNav = [
  { to: '/pipeline', label: 'Pipeline', badge: null, icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 4h12M4 8h8M6 12h4"/>
    </svg>
  )},
  { to: '/marketing', label: 'Active — Marketing', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 11L5.5 7l3 2.5L12 4"/><circle cx="12" cy="4" r="1.5" fill="currentColor" stroke="none"/>
    </svg>
  )},
  { to: '/development', label: 'Active — Dev', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M5 4L2 8l3 4M11 4l3 4-3 4M9 3l-2 10"/>
    </svg>
  )},
  { to: '/hosting', label: 'Active — Hosting', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="5" width="12" height="3.5" rx="1"/><rect x="2" y="10" width="12" height="3.5" rx="1"/>
      <circle cx="5" cy="6.75" r="0.75" fill="currentColor" stroke="none"/>
      <circle cx="5" cy="11.75" r="0.75" fill="currentColor" stroke="none"/>
    </svg>
  )},
  { to: '/paused', label: 'Archive', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="6"/><path d="M6.5 6v4M9.5 6v4"/>
    </svg>
  )},
]

const financeNav = [
  { to: '/expenses', label: 'Expenses', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="3" width="12" height="10" rx="1.5"/><path d="M2 6.5h12"/><path d="M5 9.5h3"/>
    </svg>
  )},
]

const workspaceNav = [
  { to: '/resources', label: 'Resources', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 2h7l3 3v9a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z"/><path d="M10 2v3h3"/><path d="M5 8h6M5 11h4"/>
    </svg>
  )},
]

export default function Layout() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [dots, setDots] = React.useState({})

  React.useEffect(() => {
    const today = new Date().toISOString().slice(0, 10)
    async function loadDots() {
      const in30 = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() + 30).toISOString().slice(0, 10)
      const [svcRes, msRes, pRes] = await Promise.all([
        supabase.from('services').select('service_type,payment_status,next_billing_date,domain_renewal_date,status'),
        supabase.from('milestones').select('payment_status,due_date'),
        supabase.from('pipeline').select('stage,follow_up_date,requirements_done,requirements_due'),
      ])
      const svcData = (svcRes.data || []).filter(s => !['Paused','Stopped'].includes(s.status))
      const mktDot = svcData.filter(s => s.service_type === 'Marketing').some(s => s.payment_status !== 'Received' && s.next_billing_date && s.next_billing_date < today)
      const devDot = (msRes.data || []).some(m => m.payment_status !== 'Received' && m.due_date && m.due_date < today)
      const hostingDot = svcData.filter(s => s.service_type === 'Hosting').some(s => s.domain_renewal_date && s.domain_renewal_date <= in30)
      const pipe = pRes.data || []
      const pipeDot = pipe.some(l => !['Won','Lost'].includes(l.stage) && (
        (l.follow_up_date && l.follow_up_date < today) ||
        (!l.requirements_done && l.requirements_due && l.requirements_due < today)
      ))
      setDots({ '/marketing': mktDot, '/development': devDot, '/hosting': hostingDot, '/pipeline': pipeDot })
    }
    loadDots()
  }, [])

  async function signOut() {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const initials = user?.email?.slice(0, 2).toUpperCase() || 'U'

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-top">
          <div className="sidebar-brand">
            <img src="/logo-symbol.png" alt="Aryanss" style={{ width:34, height:34, objectFit:'contain' }} />
            <div>
              <div className="brand-name">Aryanss</div>
              <div className="brand-sub">Project Tracker</div>
            </div>
          </div>

          <nav className="sidebar-nav">
            <div style={{ marginBottom: 12 }}>
              <input
                placeholder="Search…"
                onKeyDown={e => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    navigate('/search?q=' + encodeURIComponent(e.target.value.trim()))
                    e.target.value = ''
                  }
                }}
                style={{ width: '100%', padding: '6px 10px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, color: 'var(--text)', boxSizing: 'border-box', outline: 'none' }}
              />
            </div>
            <div className="nav-group-label">Overview</div>
            {nav.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end} className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
                {item.icon}<span>{item.label}</span>
              </NavLink>
            ))}

            <div className="nav-group-label">Projects</div>
            {projectNav.map(item => (
              <NavLink key={item.to} to={item.to} className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
                {item.icon}<span style={{ flex: 1 }}>{item.label}</span>
                {dots[item.to] && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--danger)', flexShrink: 0 }} />}
              </NavLink>
            ))}

            <div className="nav-group-label">Finance</div>
            {financeNav.map(item => (
              <NavLink key={item.to} to={item.to} className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
                {item.icon}<span>{item.label}</span>
              </NavLink>
            ))}

            <div className="nav-group-label">Workspace</div>
            {workspaceNav.map(item => (
              <NavLink key={item.to} to={item.to} className={({isActive}) => 'nav-item' + (isActive ? ' active' : '')}>
                {item.icon}<span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="sidebar-bottom">
          <div className="user-row">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <div className="user-email">{user?.email}</div>
              <div className="user-role">Team member</div>
            </div>
          </div>
          <button className="signout-btn" onClick={signOut}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M10 8H3M6 5l-3 3 3 3M9 4V3a1 1 0 011-1h3a1 1 0 011 1v10a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1"/>
            </svg>
            Sign out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  )
}
